import { LOCATION_TYPE_TITLE, Term } from '@/types/models'
import { PageType } from '@/types/models/PageType.model'
import getCloudinaryUrl from '@/utils/getCloudinaryUrl'
import React from 'react'
import { isLocationPageType } from '@/domain/useCases/pageTypeCheck'
import normalizeSearchState from '@/domain/search/normalizeSearchState'
import { navigateToBrowsePageWithQueryParams } from '@/domain/search/navigateToBrowsePageWithQueryParams'
import { shouldIncludeHref } from '@/domain/location/useCases/shouldIncludeHref'
import { getIndexTypeForTerm } from '@/domain/term/useCases/getIndexTypeForTerm'
import {
  DEFAULT_AROUND_RADIUS_IN_METERS,
  DEFAULT_LOCATION_TYPE,
  DISTANCE_OPTIONS_LOCATION_TYPE_MAPPING
} from '@/domain/filterOptions/constants'
import { getLocaleDistanceOptionByIndex } from '@/utils/search/getLocaleDistanceOptions'
import computeMilesToMeters from '@/utils/computeMilesToMeters'
import { mapTaxonomyToRoute } from '@/domain/useCases/taxonomyBadges/matchTreatmentToRoute'
import mapPostgresTermWithAlogliaFacetType from '@/domain/term/mapPostgresTermTypeWithAloglia'

import Carousel from '@/components/MiniCarousel/Carousel'
/**
 * Represents a treatment location with its associated metadata.
 */
export type RelatedLocation = {
  count: number
  hero_image: string
  slug: string
  title: string
  type: LOCATION_TYPE_TITLE
  miRadius: number
  locationParts?: string
  address_parts?: Partial<Location>[]
  geoLoc: {
    lat: number
    lng: number
  }
}

/**
 * Represents the data structure for rendering a location card.
 */
type Card = {
  href?: string
  onClick?: () => void
  bgImage: string
  title: string
  tooltip: string
}

const TopTreatmentLocations: React.FC<{
  locations: RelatedLocation[]
  pageType: PageType
  term: Term
}> = ({ locations, pageType, term }) => {
  const createCard = (location: RelatedLocation): Card => {
    const bgImage = getCloudinaryUrl(
      location.hero_image.split('/').pop(),
      'e_gen_restore/c_fill,w_640,h_400,q_15'
    )
    const title = location.title
    const tooltip = isLocationPageType(pageType)
      ? `${title} Treatment`
      : `${title} ${term.title} Treatment`
    const distance =
      DISTANCE_OPTIONS_LOCATION_TYPE_MAPPING[
        location.type || DEFAULT_LOCATION_TYPE
      ]
    const shouldUseCustomDistance = location.miRadius && location.miRadius > 0
    const distanceOption = shouldUseCustomDistance
      ? {
          meters: computeMilesToMeters(location.miRadius)
        }
      : getLocaleDistanceOptionByIndex(distance)
    const onClick = () => {
      navigateToBrowsePageWithQueryParams(
        normalizeSearchState({
          refinementList: {
            locations: [],
            [mapPostgresTermWithAlogliaFacetType(term.type)]: term.title
          },
          aroundLatLng: `${location.geoLoc.lat},${location.geoLoc.lng}`,
          locationName: location.locationParts,
          locationType: location.type,
          configure: {
            aroundLatLng: `${location.geoLoc.lat},${location.geoLoc.lng}`,
            aroundRadius:
              distanceOption.meters ?? DEFAULT_AROUND_RADIUS_IN_METERS,
            aroundPrecision:
              distanceOption.meters ?? DEFAULT_AROUND_RADIUS_IN_METERS
          },
          ...(shouldUseCustomDistance && {
            customLocationRadiusInMiles: location.miRadius.toString()
          })
        })
      )
    }

    return { bgImage, title, tooltip, onClick }
  }

  const cards: Card[] = locations
    .filter((location) => location && location.hero_image && location.count > 0)
    .map((location) => {
      const card = createCard(location)
      const indexType = getIndexTypeForTerm(term)

      if (shouldIncludeHref(location, indexType)) {
        const href = isLocationPageType(pageType)
          ? `/${location.slug}/`
          : location.slug === 'united-states'
          ? mapTaxonomyToRoute(term.type, term.slug)
          : `/${location.slug}/${term.slug}/`

        return {
          ...card,
          href
        }
      }

      return card
    })

  return (
    <div>
      <Carousel
        id={`top-treatment-locations`}
        title="Top Treatment Locations"
        itemsCount={cards.length}
        topArrowPosition="8"
        smallArrows
        titleClassName={'leading-8'}
      >
        {cards.map((card) => (
          <CardComponent
            key={card.title}
            card={card}
          />
        ))}
      </Carousel>
    </div>
  )
}

const CardComponent: React.FC<{ card: Card }> = ({ card }) => {
  const Component = card.href ? 'a' : 'div'
  const componentProps = card.href
    ? { href: card.href }
    : { onClick: card.onClick }

  return (
    <div className="mr-4 min-w-[124px] cursor-pointer items-center overflow-hidden last:mr-0 md:h-[128px] md:min-w-[164px] md:max-w-[164px]">
      <Component
        className="relative block overflow-hidden rounded-xl"
        {...componentProps}
        title={card.tooltip}
      >
        <img
          alt={card.tooltip}
          src={card.bgImage}
          className="flex h-[80px] w-full items-center justify-center rounded-xl object-cover md:h-[96px] md:max-h-[96px]"
          loading="lazy"
        />
        <div className="mt-2 flex w-full flex-nowrap gap-0.5">
          <span className="truncate text-sm text-gray-1100 md:text-base">
            {card.title}
          </span>
        </div>
      </Component>
    </div>
  )
}

export default TopTreatmentLocations
